import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { GroupBase } from '../../../models/blueprint.model';
import {
  DataObject,
  RecordUpdateEmission,
  ReportInfo,
  ReportRecord,
  ReportView,
  TemplateProjectionMap,
} from '../../../models/report.model';
import { TemplateField } from '../../../models/template.model';
import { ReportFieldFormatterService } from '../../../report-field-formatter.service';
import { ADDITIONAL_IMAGE_FIELD_ID, IMAGE_FIELD_ID, IMAGE_STATIC } from '../../../shared/templates/config/common';
import { EditableFieldsService } from '../../editable-fields.service';
import { FLOORPLAN_DISPLAY_TEMPLATES } from './record-body.model';
import { onDrop } from '../../presentation-dynamic-view/presentation-report.helper';
import { Router } from '@angular/router';
import { digger } from '@cbre/digger';

@Component({
  selector: 'ppt-record-body',
  templateUrl: './record-body.component.html',
  styleUrls: ['./record-body.component.scss'],
  standalone: false,
})
export class RecordBodyComponent implements OnChanges {
  @Input() recordNumber: number;
  @Input() reportData: ReportInfo;
  @Input() currentView: ReportView;
  @Input() headerGroups: GroupBase<TemplateField>[];
  @Input() groupsToDisplay: GroupBase<TemplateField>[];
  @Input() customConfigs: { symbols: DataObject };
  @Input() confidentialityTmpl: TemplateRef<any>;
  @Input() reportLocale: number;
  @Input() imageGroups: GroupBase<TemplateField>[];
  @Output() reportUpdateClick: EventEmitter<RecordUpdateEmission> = new EventEmitter<RecordUpdateEmission>();
  @Output() removeRecord: EventEmitter<ReportRecord> = new EventEmitter<ReportRecord>();
  @Output() reportReordered: EventEmitter<ReportRecord[]> = new EventEmitter<ReportRecord[]>();
  @Input() templateProjectionMap: TemplateProjectionMap;

  reportRecordData: ReportRecord;
  allFields: TemplateField[];
  headerFields: TemplateField[];
  tableFields: GroupBase<TemplateField>;
  bodyAttributes: GroupBase<TemplateField>;
  allDataFields: TemplateField[];
  floorPlanTemplateIds = false;
  tableData: any[];
  isSecondaryImageRequired = false;

  constructor(
    public editableFieldsService: EditableFieldsService,
    private reportFieldFormatterService: ReportFieldFormatterService,
    private router: Router
  ) {}

  ngOnChanges({ recordNumber, _reportLocale, currentView, reportData, groupsToDisplay }: SimpleChanges) {
    this.editableFieldsService.mapEditableFields(this.groupsToDisplay);
    if (
      (!!reportData && !!reportData.currentValue) ||
      (!!recordNumber && !!recordNumber.currentValue) ||
      (!!currentView && !!currentView.currentValue) ||
      (!!groupsToDisplay && !!groupsToDisplay.currentValue)
    ) {
      this.reportRecordData =
        this.reportData && this.recordNumber ? this.reportData.records[this.recordNumber - 1] : null;
      this.isSecondaryImageRequired = [43, 44].includes(this.currentView.templateId);
      if (this.isSecondaryImageRequired && !!this.groupsToDisplay) {
        const [bodyAttributes, tableFields] = this.groupsToDisplay;
        this.tableFields = tableFields;
        const fields = bodyAttributes.fields.filter(field => {
          return this.reportFieldFormatterService.isFieldVisible(this.reportRecordData, {
            templateId: this.currentView.templateId,
            field,
          });
        });
        this.bodyAttributes = { ...bodyAttributes, fields };
        this.allFields = [].concat(...(bodyAttributes ? [bodyAttributes] : []).map(group => group.fields));
      } else {
        this.allFields = [].concat(...(this.groupsToDisplay || []).map(group => group.fields));
      }
      this.allDataFields = (this.allFields || []).filter(
        field => field.id !== IMAGE_FIELD_ID && field.id !== ADDITIONAL_IMAGE_FIELD_ID && field.id !== IMAGE_STATIC
      );
      this.headerFields = []
        .concat(...(this.headerGroups || []).map(group => group.fields))
        .sort((a, b) => a.options.headerSortOrder - b.options.headerSortOrder);
      this.floorPlanTemplateIds =
        this.reportData.entity === 'availability' && FLOORPLAN_DISPLAY_TEMPLATES.has(this.currentView.templateId);
      if (!!this.reportData && !!this.reportData.records && this.tableFields && this.tableFields.tableConfigs) {
        const data = digger(this.reportRecordData, this.tableFields.tableConfigs.dataKey);
        this.tableData = Array.isArray(data) ? data : null;
      }
    }
  }

  emitEditableFields(event: MouseEvent, id: number) {
    const emittedObject = this.editableFieldsService.generateRecordUpdateEmission(
      event,
      id,
      this.reportRecordData.data
    );
    if (emittedObject != null) {
      emittedObject.locale = this.reportLocale;
      this.reportUpdateClick.emit(emittedObject);
    }
  }

  goToRecordDetails(recordData: DataObject) {
    // TODO (Ramakrishna): need to implement pipe as like in condensed view for link navigation
    const { entity } = this.reportData;
    const currentRecordId = recordData.entity.id;
    const url = `/records/${entity}/${currentRecordId}/details`;
    this.router.navigateByUrl(url);
  }

  public onReorder(cdkDragDropEvent: CdkDragDrop<Array<{ [key: string]: any }>>, recordData) {
    onDrop(cdkDragDropEvent, recordData, this.reportData, this.reportReordered, this.recordNumber);
  }
}
